import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Plus, Phone, Mail, Download, X } from "lucide-react";
import api, { API, formatApiError } from "../../lib/api";
import { PageHeader, Card, Badge, Empty, RedButton, GhostButton, Field, inputCls } from "../../components/crm/ui";
import { toast } from "sonner";

export default function Contacts() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [source, setSource] = useState("");
  const [stage, setStage] = useState("");
  const [page, setPage] = useState(1);
  const [sources, setSources] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [showAdd, setShowAdd] = useState(params.get("quick") === "1");

  const load = useCallback(() => {
    api.get("/contacts", { params: { q, source, stage, page, page_size: 25 } })
      .then(({ data }) => setData(data)).catch(() => {});
  }, [q, source, stage, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/sources").then(({ data }) => setSources(data.items)).catch(() => {});
    api.get("/pipelines").then(({ data }) => setPipeline(data.items[0])).catch(() => {});
  }, []);

  const exportCsv = () => {
    const token = localStorage.getItem("alto_token");
    fetch(`${API}/export/contacts`, { headers: { Authorization: `Bearer ${token}`, "X-Org-Id": localStorage.getItem("alto_org") } })
      .then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = "contacts.csv"; a.click(); });
  };

  const stages = pipeline?.stages?.map((s) => s.name) || [];

  return (
    <div>
      <PageHeader title="Contacts" subtitle={`${data.total} contact${data.total === 1 ? "" : "s"}`}>
        <GhostButton onClick={exportCsv} data-testid="contacts-export"><Download size={15} /> Export</GhostButton>
        <RedButton onClick={() => setShowAdd(true)} data-testid="contacts-add"><Plus size={15} /> Add Contact</RedButton>
      </PageHeader>

      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name, email, phone…" data-testid="contacts-search" className={inputCls + " pl-9"} />
          </div>
          <select value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }} data-testid="contacts-filter-source" className={inputCls + " sm:w-48"}>
            <option value="">All sources</option>
            {sources.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
          <select value={stage} onChange={(e) => { setStage(e.target.value); setPage(1); }} data-testid="contacts-filter-stage" className={inputCls + " sm:w-48"}>
            <option value="">All stages</option>
            {stages.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </Card>

      {data.items.length === 0 ? (
        <Card className="p-6"><Empty icon={Search} title="No contacts found" hint="Add a contact, build a form, or import a CSV to get started." action={<RedButton onClick={() => setShowAdd(true)}><Plus size={15} /> Add Contact</RedButton>} /></Card>
      ) : (
        <>
          {/* Desktop table */}
          <Card className="hidden md:block overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left font-semibold px-5 py-3">Name</th>
                  <th className="text-left font-semibold px-5 py-3">Contact</th>
                  <th className="text-left font-semibold px-5 py-3">Original Source</th>
                  <th className="text-left font-semibold px-5 py-3">Stage</th>
                  <th className="text-left font-semibold px-5 py-3">Next Follow-Up</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50" data-testid={`contact-row-${c.id}`}>
                    <td className="px-5 py-3"><Link to={`/app/contacts/${c.id}`} className="font-semibold text-slate-900 hover:text-alto-red">{c.first_name} {c.last_name}</Link></td>
                    <td className="px-5 py-3 text-slate-600">
                      <div className="flex flex-col gap-0.5">
                        {c.phone && <a href={`tel:${c.phone}`} className="hover:text-alto-red flex items-center gap-1.5"><Phone size={12} />{c.phone}</a>}
                        {c.email && <a href={`mailto:${c.email}`} className="hover:text-alto-red flex items-center gap-1.5 truncate max-w-[200px]"><Mail size={12} />{c.email}</a>}
                      </div>
                    </td>
                    <td className="px-5 py-3"><Badge>{c.original_source || "—"}</Badge></td>
                    <td className="px-5 py-3 text-slate-600">{c.stage || "—"}</td>
                    <td className="px-5 py-3 text-slate-600">{c.next_follow_up ? c.next_follow_up.slice(0, 10) : <span className="text-alto-red font-medium">None</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {data.items.map((c) => (
              <Card key={c.id} className="p-4" data-testid={`contact-card-${c.id}`}>
                <div className="flex items-center justify-between">
                  <Link to={`/app/contacts/${c.id}`} className="font-semibold text-slate-900">{c.first_name} {c.last_name}</Link>
                  <Badge>{c.original_source || "—"}</Badge>
                </div>
                <div className="mt-2 flex gap-3 text-sm text-slate-600">
                  {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1.5"><Phone size={13} />Call</a>}
                  {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1.5"><Mail size={13} />Email</a>}
                </div>
              </Card>
            ))}
          </div>

          {data.total > 25 && (
            <div className="flex items-center justify-between mt-4">
              <GhostButton disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</GhostButton>
              <span className="text-sm text-slate-500">Page {page} of {Math.ceil(data.total / 25)}</span>
              <GhostButton disabled={page >= Math.ceil(data.total / 25)} onClick={() => setPage(page + 1)}>Next</GhostButton>
            </div>
          )}
        </>
      )}

      {showAdd && <AddContactModal sources={sources} pipeline={pipeline} onClose={() => { setShowAdd(false); params.delete("quick"); setParams(params); }} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddContactModal({ sources, pipeline, onClose, onSaved }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", source: "CRM Form", stage: "", tags: [] });
  const [dup, setDup] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const checkDup = async () => {
    if (!form.email && !form.phone) return;
    const { data } = await api.post("/contacts/check-duplicate", form);
    setDup(data.duplicate);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/contacts", form);
      toast.success("Contact saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-bold text-slate-900">Add Contact</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name"><input className={inputCls} value={form.first_name} onChange={set("first_name")} data-testid="add-first-name" /></Field>
            <Field label="Last name"><input className={inputCls} value={form.last_name} onChange={set("last_name")} data-testid="add-last-name" /></Field>
          </div>
          <Field label="Email"><input className={inputCls} value={form.email} onChange={set("email")} onBlur={checkDup} data-testid="add-email" /></Field>
          <Field label="Phone"><input className={inputCls} value={form.phone} onChange={set("phone")} onBlur={checkDup} data-testid="add-phone" /></Field>
          {dup && <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800" data-testid="dup-warning">Possible duplicate: <Link to={`/app/contacts/${dup.id}`} className="font-semibold underline">{dup.first_name} {dup.last_name}</Link></div>}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Source"><select className={inputCls} value={form.source} onChange={set("source")} data-testid="add-source">{sources.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}</select></Field>
            <Field label="Stage"><select className={inputCls} value={form.stage} onChange={set("stage")} data-testid="add-stage"><option value="">—</option>{(pipeline?.stages || []).map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}</select></Field>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <GhostButton className="flex-1" onClick={onClose}>Cancel</GhostButton>
          <RedButton className="flex-1" onClick={save} disabled={saving} data-testid="add-save">{saving ? "Saving…" : "Save Contact"}</RedButton>
        </div>
      </Card>
    </div>
  );
}
